// @flow
import * as React from 'react';
import ColumnResizeHelper, {
    ScreenSizeStates
} from 'common/components/core/ColumnResizer/ColumnResizeHelper';
import variables from 'common/components/core/variables.scss';
import * as LocalStorage from 'common/utils/LocalStorage';
import type { ScreenSizeStatesType } from 'common/components/core/ColumnResizer/ColumnResizeHelper';

const GANTT_MIN_WIDTH = 300;
const DEFAULT_GANTT_AREA_RATIO = 0.6;

const getContainerWidth = () => window.innerWidth - variables.leftbarWidthInt;

export const useColumnResizer = (id: string) => {
    if (!id) {
        throw Error('Did you miss passing id?');
    }
    const containerWidth = getContainerWidth();
    const cachedState = LocalStorage.getItem(id);
    const [screenSizeState, setScreenSizeState] = React.useState(
        cachedState || ScreenSizeStates.Default
    );
    const {
        leftPaneWidth: _leftPaneWidth,
        rightPaneWidth: _rightPaneWidth,
        columnResizerPosition: _columnResizerPosition
    } = ColumnResizeHelper.getWidthsFromScreenSizeState({
        screenSizeState,
        containerWidth,
        leftPaneMinWidth: GANTT_MIN_WIDTH,
        defaultRatio: DEFAULT_GANTT_AREA_RATIO
    });

    const [nextScreenSizeState, setNextScreenSizeState] = React.useState(
        ScreenSizeStates.Default
    );
    const [leftPaneWidth, setLeftPaneWidth] = React.useState(_leftPaneWidth);
    const [rightPaneWidth, setRightPaneWidth] = React.useState(_rightPaneWidth);
    const [columnResizerPosition, setColumnResizerPosition] = React.useState(
        _columnResizerPosition
    );
    const [isResizing, setIsResizing] = React.useState(false);

    const updateWidths = React.useCallback(() => {
        const containerWidth = getContainerWidth();
        const {
            leftPaneWidth: _leftPaneWidth,
            rightPaneWidth: _rightPaneWidth,
            columnResizerPosition: _columnResizerPosition
        } = ColumnResizeHelper.getWidthsFromScreenSizeState({
            screenSizeState,
            containerWidth,
            leftPaneMinWidth: GANTT_MIN_WIDTH,
            defaultRatio: DEFAULT_GANTT_AREA_RATIO
        });

        setNextScreenSizeState(null);
        setLeftPaneWidth(_leftPaneWidth);
        setRightPaneWidth(_rightPaneWidth);
        setColumnResizerPosition(_columnResizerPosition);
    }, [screenSizeState]);

    const onColumnResize = React.useCallback(
        (e, data) => {
            const containerWidth = getContainerWidth();
            const { x } = data;

            const newScreenSizeState = ColumnResizeHelper.getNewScreenSizeState(
                {
                    screenSizeState,
                    currentXPos: x,
                    containerWidth,
                    leftPaneMinWidth: GANTT_MIN_WIDTH,
                    defaultRatio: DEFAULT_GANTT_AREA_RATIO
                }
            );
            setIsResizing(true);
            setNextScreenSizeState(newScreenSizeState);
        },
        [screenSizeState]
    );

    const onColumnResizeStop = React.useCallback(
        (e, data) => {
            const containerWidth = getContainerWidth();
            const { x } = data;

            const newScreenSizeState = ColumnResizeHelper.getNewScreenSizeState(
                {
                    screenSizeState,
                    currentXPos: x,
                    containerWidth,
                    leftPaneMinWidth: GANTT_MIN_WIDTH,
                    defaultRatio: DEFAULT_GANTT_AREA_RATIO
                }
            );

            setIsResizing(false);
            setScreenSizeState(newScreenSizeState);
        },
        [screenSizeState]
    );

    const onWindowResize = React.useCallback(() => {
        updateWidths();
    }, [updateWidths]);

    React.useEffect(() => {
        updateWidths();
    }, [updateWidths]);

    React.useEffect(() => {
        window.addEventListener('resize', onWindowResize);
        return () => {
            window.removeEventListener('resize', onWindowResize);
        };
    }, [onWindowResize]);

    const _setScreenSizeState = React.useCallback(
        (value: ScreenSizeStatesType) => {
            LocalStorage.setItem(id, value);
            setScreenSizeState(value);
        },
        [id]
    );

    return {
        screenSizeState,
        setScreenSizeState: _setScreenSizeState,
        onColumnResize,
        onColumnResizeStop,

        nextScreenSizeState,
        leftPaneWidth,
        rightPaneWidth,
        columnResizerPosition,
        isResizing
    };
};
